/**
 * Mixin for connector logs search query backed by URL param `q`.
 * - Initializes from URL on mount; syncs back to URL with 150ms debounce via replace.
 * - Keeps `?q=` when input is cleared (explicit empty state).
 * - Provides filteredConnectorLogs computed for case-insensitive substring filtering.
 * - Components using this mixin expose: searchQuery (data), updateSearchQuery (method), filteredConnectorLogs (computed).
 * - Components must provide connectorLogs (array of log entries), activeItemId, and logItemPayload for selection reconciliation.
 * - When filteredConnectorLogs changes, selection is cleared if the selected entry is no longer visible.
 */
import debounce from 'lodash/debounce';

const SEARCH_QUERY_PARAM = 'q';
const DEBOUNCE_MS = 150;

/**
 * Build searchable text from a connector log entry for case-insensitive substring matching.
 * Includes: id, createdAt (raw ISO), payload (stringified), meta (stringified).
 */
export function getSearchableText(entry) {
  if (!entry || typeof entry !== 'object') {
    return '';
  }
  const parts = [
    entry.id != null ? String(entry.id) : '',
    entry.createdAt != null ? String(entry.createdAt) : '',
    entry.payload != null
      ? typeof entry.payload === 'object'
        ? JSON.stringify(entry.payload)
        : String(entry.payload)
      : '',
    entry.meta != null ? (typeof entry.meta === 'object' ? JSON.stringify(entry.meta) : String(entry.meta)) : '',
  ];
  return parts.join(' ').toLowerCase();
}

export const connectorLogsSearchQueryMixin = {
  data() {
    return {
      searchQuery: '',
      pendingRouteQuerySyncs: [],
    };
  },
  created() {
    const q = this.$route.query[SEARCH_QUERY_PARAM];
    this.searchQuery = q != null ? String(q) : '';
    this.debouncedSyncSearchQueryToUrl = debounce((val) => this.syncSearchQueryToUrl(val), DEBOUNCE_MS);
  },
  beforeDestroy() {
    if (this.debouncedSyncSearchQueryToUrl?.cancel) {
      this.debouncedSyncSearchQueryToUrl.cancel();
    }
  },
  computed: {
    filteredConnectorLogs() {
      const entries = this.connectorLogs || [];
      const query = (this.searchQuery || '').trim().toLowerCase();
      if (!query) {
        return entries;
      }
      return entries.filter((entry) => getSearchableText(entry).includes(query));
    },
  },
  watch: {
    [`$route.query.${SEARCH_QUERY_PARAM}`](newVal) {
      const val = newVal != null ? String(newVal) : '';
      const pendingRouteQuerySyncIndex = this.pendingRouteQuerySyncs.findIndex(
        (pendingRouteQuerySync) => pendingRouteQuerySync.query === val
      );
      if (pendingRouteQuerySyncIndex !== -1) {
        this.pendingRouteQuerySyncs.splice(pendingRouteQuerySyncIndex, 1);
        return;
      }
      if (this.searchQuery !== val) {
        this.searchQuery = val;
      }
    },
    filteredConnectorLogs: {
      handler(visible) {
        if (this.activeItemId == null) {
          return;
        }
        const isVisible = visible.some((entry) => entry.id === this.activeItemId);
        if (!isVisible) {
          this.activeItemId = null;
          this.logItemPayload = null;
        }
      },
    },
  },
  methods: {
    updateSearchQuery(val) {
      this.searchQuery = val;
      this.debouncedSyncSearchQueryToUrl(val);
    },
    syncSearchQueryToUrl(val) {
      const q = val != null ? String(val) : '';
      const pendingRouteQuerySync = { query: q };
      this.pendingRouteQuerySyncs.push(pendingRouteQuerySync);
      const clearPendingRouteQuerySync = () => {
        const pendingRouteQuerySyncIndex = this.pendingRouteQuerySyncs.indexOf(pendingRouteQuerySync);
        if (pendingRouteQuerySyncIndex !== -1) {
          this.pendingRouteQuerySyncs.splice(pendingRouteQuerySyncIndex, 1);
        }
      };
      return this.$router
        .replace({
          query: {
            ...this.$route.query,
            [SEARCH_QUERY_PARAM]: q,
          },
        })
        .then(() => {
          this.$nextTick(clearPendingRouteQuerySync);
        })
        .catch((err) => {
          clearPendingRouteQuerySync();
          console.warn('connectorLogsSearchQuery: router.replace', err);
        });
    },
  },
};
